import {
    createSelector,
    createSlice,
    type PayloadAction,
} from "@reduxjs/toolkit";

import { RootState } from "~redux/store";
import type { Story } from "~types/types";

interface storiesState {
    storiesList: Story[];
}

const initialState: storiesState = {
    storiesList: [],
};

const storiesSlice = createSlice({
    name: "stories",
    initialState,
    reducers: {
        storyFetched(state, action: PayloadAction<Story>) {
            const story = action.payload;
            const storiesList = [...state.storiesList];
            const matchingStory = storiesList.find((s) => s.id === story.id);

            if (story.body && !matchingStory) {
                storiesList.push(story);
            }

            return { storiesList };
        },
        pageViewTracked(state, action: PayloadAction<Story>) {
            const story = action.payload;
            const storiesList = state.storiesList;

            if (story.isPageTracked) return { storiesList };

            const updatedStory = {
                ...story,
                isPageTracked: true,
            };

            return {
                storiesList: storiesList.map((s) =>
                    s.id === story.id ? updatedStory : s
                ),
            };
        },
        contextualNavViewTracked(state, action: PayloadAction<Story>) {
            const story = action.payload;
            const storiesList = state.storiesList;

            if (story.isContextualNavViewTracked) return { storiesList };

            const updatedStory = {
                ...story,
                isContextualNavViewTracked: true,
            };

            return {
                storiesList: storiesList.map((s) =>
                    s.id === story.id ? updatedStory : s
                ),
            };
        },
    },
});

export const { storyFetched, pageViewTracked, contextualNavViewTracked } =
    storiesSlice.actions;

export const selectStories = (state: RootState) => state.stories.storiesList;
export const selectStoryIds = createSelector(selectStories, (storiesList) =>
    storiesList.map(({ id }) => id)
);
export const selectCurrentStory = (state: RootState) => {
    const currentStory = state.stories.storiesList.find(
        ({ slug }) => slug === state.transporter.currentSlug
    );

    if (!currentStory) {
        throw new Error(
            "Unable to find current transporter slug in stories list within Redux."
        );
    }

    return currentStory;
};

export const selectNextStory = (state: RootState) => {
    const storiesList = state.stories.storiesList || [];

    if (!storiesList.length) return null;

    const currentSlug = state.transporter.currentSlug;

    const storyIndex = storiesList.findIndex(
        (story) => story.slug === currentSlug
    );

    return storiesList[storyIndex + 1];
};

export default storiesSlice.reducer;
